import { Dispatch, SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "@/redux/hooks";
import { clearGlobalSearch } from "@/pages/Private/redux/search/search.slice";
import { useResolveProspectFromLinkedinUrlMutation } from "@/pages/Private/redux/list/list.api";
import { useLazyGetCompanyEnrichPersonQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";

import { SelectedLinkedInItem } from "./singleSelect";
import { resolveLinkedInItemUrl } from "./resolveSelectionLinkedinUrl";

interface UseAddToContactsArgs {
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
	setIsOpened: Dispatch<SetStateAction<boolean>>;
	// The modal's snackbar opener — useSnackBar is per-instance and only the modal's is rendered.
	handleOpenSnackbar: (text: string, autoHideDuration?: number) => void;
	// Fired after a successful add — the modal bumps its addedSignal so the By URL tab resets.
	onAddSuccess: () => void;
	// Fired after every attempt (success or failure) — the modal closes the add-to popover.
	onAddSettled: () => void;
}

interface UseAddToContactsResult {
	isAddingToContacts: boolean;
	handleAddToContacts: () => Promise<void>;
}

/**
 * "Add to contact" flow of GlobalSearchModal: makes the selected people exist as prospects in the
 * company's contact database **without** adding them to any list. Selected existing contacts already
 * live in Contacts, so only the LinkedIn-sourced selections are resolved to a URL and created via
 * `resolveProspectFromLinkedinUrl` (which writes a prospect but no list_person). Kept out of the
 * modal to keep that component under the file-size limit.
 */
export function useAddToContacts({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	setIsOpened,
	handleOpenSnackbar,
	onAddSuccess,
	onAddSettled,
}: UseAddToContactsArgs): UseAddToContactsResult {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const [resolveProspectFromLinkedinUrl, { isLoading: isAddingToContacts }] =
		useResolveProspectFromLinkedinUrlMutation();
	const [resolveCompanyEnrichPerson] = useLazyGetCompanyEnrichPersonQuery();

	const handleAddToContacts = useCallback(async () => {
		try {
			// LinkedIn-sourced selections aren't prospects yet: resolve each to a URL, then create
			// the contact from it. Existing selected contacts already live in Contacts (counted below).
			const urls = await Promise.all(
				selectedLinkedInItems.map((li) =>
					resolveLinkedInItemUrl(li, (companyEnrichId) =>
						resolveCompanyEnrichPerson(companyEnrichId)
							.unwrap()
							.catch(() => undefined)
					)
				)
			);

			const resolvedUrls = urls.filter((url): url is string => !!url);

			// Nothing to create and no existing contacts selected — surface an error and keep the
			// modal open so the user can adjust the selection.
			if (resolvedUrls.length === 0 && selectedContactIds.length === 0) {
				handleOpenSnackbar(t("basics.error"));

				return;
			}

			const created = await Promise.all(
				resolvedUrls.map((linkedinUrl) =>
					resolveProspectFromLinkedinUrl({ linkedinUrl })
						.unwrap()
						.then(() => true)
						.catch(() => false)
				)
			);

			const createdCount = created.filter(Boolean).length;
			const totalInContacts = createdCount + selectedContactIds.length;

			if (totalInContacts === 0) {
				handleOpenSnackbar(t("basics.error"));

				return;
			}

			handleOpenSnackbar(
				`Added ${totalInContacts} ${totalInContacts === 1 ? "contact" : "contacts"} to your contacts.`,
				6000
			);
			onAddSuccess();
			dispatch(clearGlobalSearch());
			setIsOpened(false);
		} catch {
			handleOpenSnackbar(t("basics.error"));
		} finally {
			onAddSettled();
			setSelectedContactIds([]);
			setSelectedLinkedInItems([]);
		}
	}, [
		selectedLinkedInItems,
		selectedContactIds,
		resolveProspectFromLinkedinUrl,
		resolveCompanyEnrichPerson,
		handleOpenSnackbar,
		t,
		onAddSuccess,
		onAddSettled,
		dispatch,
		setIsOpened,
		setSelectedContactIds,
		setSelectedLinkedInItems,
	]);

	return { isAddingToContacts, handleAddToContacts };
}
